import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { GlobalsService as GlobalsServiceOld } from '@services/globals.service';
import { GlobalsService } from '@app/services/micro/globals.service';
import { MessagesService } from '@services/messages.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FlightDetailModalComponent } from '@modals/flight-detail-modal/flight-detail-modal.component';
import { LookUpPnrService } from '@app/services/micro/lookup-pnr.service';
import { PaymentDeviceService } from '@app/services/devices/payment-device.service';
import { IropsService } from '@app/services/micro/irops.service';
import { CartService } from '@app/services/micro/cart.service';
import { SeatPassangerEstatus } from '@app/enums';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
/**
  Componente que representa la barra de navegación de la aplicación.
  Este componente muestra un menú superior con opciones como el logo de la aplicación,
  botones para reiniciar la sesión, reanudar el vuelo, y acceder a detalles del vuelo.
  También permite personalizar la visibilidad de ciertos elementos según el contexto.
**/
@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  standalone: false,
})
export class NavbarComponent implements OnInit, OnChanges {
  @Input() showStepper = false;
  @Input() step = 0;
  @Input() showTextLenguages = true;
  /**
    Indica si se debe mostrar el botón de selección de idiomas.
    @type {boolean}
    @default false
  **/
  @Input() showLanguagesButton = false;

  /**
      Indica si se debe mostrar el ícono corto en lugar del logo completo.
      @type {boolean}
      @default false
    **/
  @Input() showShortIcon = false;

  /**
      Indica si la barra de navegación está en un contexto de RB (Reembolso).
      @type {boolean}
      @default false
    **/
  @Input() isrb = false;

  /**
      Indica si se debe mostrar el botón de "Comenzar de nuevo".
      @type {boolean}
      @default false
    **/
  @Input() showStartOver = false;

  /**
      Indica si se debe mostrar el botón de "Reanudar vuelo".
      @type {boolean}
      @default false
    **/
  @Input() showResumeFlight = false;

  /**
      Indica si se debe mostrar el botón de "Sky Priority".
      @type {boolean}
      @default false
    **/
  @Input() showSkyPriority = false;

  /**
      Estado actual de la barra de navegación (por ejemplo, para indicar progreso o estado de la reserva).
      @type {number}
      @default 50
    **/
  @Input() status = 50;

  /**
      Ruta del logo que se mostrará en la barra de navegación.
      @type {string}
    **/
  logoURL: string;
  idLang: string;

  constructor(
    private router: Router,
    private global: GlobalsService,
    private _globalServicesOld: GlobalsServiceOld,
    private _messages: MessagesService,
    private _modal: NgbModal,
    private _lookUpPnrService: LookUpPnrService,
    private _paymentDevice: PaymentDeviceService,
    private _iropsService: IropsService,
    private _cart: CartService
  ) {}

  ngOnInit(): void {
    this.logoURL = `assets/images/${this.showShortIcon ? 'shortIcon.svg' : 'Logo_AMlogo.svg'}`;
    this.global.language$.subscribe({
      next: (id) => {
        this.idLang = id;
      },
    });
  }
  /**
    Maneja el evento de clic en el botón "Comenzar de nuevo".
    Limpia todos los datos de la sesión actual, cierra los mensajes abiertos y redirige al usuario a la página de inicio.
    @return void
  **/
  onStarOver() {
    this.rollbackUnpaidSeats().subscribe({
      next: () => this.completeStartOver(),
      error: () => this.completeStartOver(),
    });
  }

  /**
   * Rolls back unpaid seat assignments on the backend before clearing session data.
   * This prevents stale seat selections from persisting when the user starts over without completing payment.
   */
  private rollbackUnpaidSeats() {
    const reservation = this.global.getReservation();
    if (!reservation || !reservation.passengers || reservation.passengers.length === 0) {
      return of(null);
    }

    const hasUnpaidSeats = reservation.passengers.some((passenger) => {
      if (!(passenger.seatsNew?.length > 0)) {
        return false;
      }
      return passenger.seatsNew.some(
        (seat) => seat.status === SeatPassangerEstatus.PENDING_PAYMENT
      );
    });

    if (hasUnpaidSeats) {
      reservation.passengers.forEach((passenger) => {
        if (passenger.seatsNew?.length > 0) {
          passenger.checkinSelected = true;
        }
      });

      return this._cart.buildDelete(reservation, 'seats').pipe(
        tap((response) => this._cart.removeSeatsToCart(response)),
        catchError(() => of(null))
      );
    }
    return of(null);
  }

  private completeStartOver(): void {
    this.global.clearAll();
    this._globalServicesOld.clearAll();
    this._iropsService.resetIropsState();
    this._messages.closeAll();
    this._lookUpPnrService.redirect = false;
    if (this._paymentDevice.isOK()) {
      this._paymentDevice.disable();
      this._paymentDevice.clearTransactionData();
      this._paymentDevice.cancelTransaction();
    }
    this.router.navigate(['/home']);
  }
  /**
    Método del ciclo de vida de Angular que se ejecuta cuando cambian las propiedades de entrada.
    Actualiza la ruta del logo si la propiedad `showShortIcon` cambia.
    @param {SimpleChanges} changes - Cambios en las propiedades de entrada.
    @return void
  **/
  ngOnChanges(changes: SimpleChanges): void {
    const { showShortIcon } = changes;
    if (showShortIcon?.currentValue) {
      const { currentValue } = showShortIcon;
      this.logoURL = `assets/images/${currentValue ? 'shortIcon' : 'Logo_AMlogo'}.svg`;
    }
  }
  /**
    Abre un modal con los detalles del vuelo.
    @return void
  **/
  onOpenResume() {
    this._modal.open(FlightDetailModalComponent, {
      size: 'xl',
      backdrop: 'static',
      fullscreen: 'xl',
      keyboard: false,
    });
  }
}
